import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';
import { Trans, useTranslation } from 'react-i18next';

import ChileanForm from '@/lib/components/ChileanForm';
import Clabe from '@/lib/components/Clabe';
import ColombianForm from '@/lib/components/ColombianForm';
import ErrorBlock from '@/lib/components/ErrorBlock';
import HongKongForm from '@/lib/components/HongKongForm';
import Iban from '@/lib/components/Iban';
import Ingenico from '@/lib/components/Ingenico';
import IngenicoLegacy from '@/lib/components/IngenicoLegacy';
import InternationalBank from '@/lib/components/InternationalBank';
import Loader from '@/lib/components/Loader';
import Payline from '@/lib/components/Payline';
import PaypalForm from '@/lib/components/PaypalForm';
import OutageBlock from '@/lib/components/OutageBlock';
import PaymentMethodOption from '@/lib/components/PaymentMethodOption';
import PaymentMethodOptionExpress from '@/lib/components/PaymentMethodOptionExpress';
import PaymentsOS from '@/lib/components/PaymentsOS';
import PeruvianForm from '@/lib/components/PeruvianForm/PeruvianForm';
import RevolutPay from '@/lib/components/RevolutPay';
import SouthAfricanForm from '@/lib/components/SouthAfricanForm';
import LinkPSP from '@/lib/components/LinkPSP/LinkPSP';
import i18n from '@/lib/config/i18n';
import { getClientCapabilities, lighenOrDarkenColor, getContrastText } from '@/lib/utils/browserUtils';
import { intlPriceFormat } from '@/lib/utils/currencyUtils';
import { handlePreviousPaymentAbortedIfNeeded } from '@/lib/utils/handlePreviousPaymentAborted';
import PaymentPlan from '@/lib/components/PaymentPlan/PaymentPlan';
import BackLink from '../BackLink';
import PaymentFooter from '@/lib/components/PaymentFooter';
import Nuvei from '@/lib/components/Nuvei';

import styles from './PspComposer.module.scss';
import InternationalTransfersMexico from '@/lib/components/InternationalTransfersMexico/index.js';
import { useIsSandboxMode } from '@/lib/hooks/useIsSandboxMode.js';
import PSPFailedToLoad from '@/lib/components/PSPFailedToLoad/index.js';
import StoredPaymentMethod from '@/lib/components/StoredPaymentMethod/index.js';
import { useShowSaveCard } from '@/lib/context/SaveCardContext.js';

const importAdyen = () => import('@/lib/components/Adyen');
const Adyen = React.lazy(importAdyen);

export const APPLE_PAYMENT_METHODS = ['ApplePay', 'apple_pay'];
export const GOOGLE_PAYMENT_METHODS = ['GooglePay', 'google_pay'];
export const EXPRESS_PAYMENT_METHODS = [...APPLE_PAYMENT_METHODS, ...GOOGLE_PAYMENT_METHODS, 'RevolutPay'];
const DEFAULT_LOADER = <Loader />;
export const RETRIABLE_ERRORS = ['initializationError', 'CancelledByUser', 'RetryWithTheSameCard', 'RetryWithAnotherCard', 'CheckYourCardDetails', 'AmountLimit', 'PSPError', 'PSPAPIError', 'Service reference not the same as reference from tokenized data', 'network', 'TokenChanged', 'UserChangedPaymentMethod'];


const RETRY_ATTEMPTS_FAILED_INITIALIZATION = 0;
const NOT_PAYABLE_ORDER_STATUSES = ['CANCELLED', 'TERMINATED'];

/**
 * This is the main component that is exposed to be used by front-end projects that want to include payment providers.
 */
const PspComposer = React.forwardRef(
    (
        {
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton = false,
            labelPayButton = null,  // Fallback on 'pay' or 'ask refund' label in the different components
            labelPayButtonApplePayAndGooglePay = 'buy',
            forceDisplayPaymentMethodsInExpandableButton = false,
            shouldDisplayExpressCheckoutTitle = true,
            shouldDisplayPaymentMethodsTitle = true,
            onPaymentResult,
            onConfigReady,
            lang = '',
            loader,
            expandOptionIfOnlyOne = true,
            paymentCardInputWarningMessage = null,
            primaryColor = '#0032fa',
            themeMode = 'light',
            setDisplayCallbackBlock,
            successCallbackUrl,
            cancelCallbackUrl,
            failureCallbackUrl,
            bankStatement,
            paymentRequestToken,
            isCheckoutPlan = false,
            setDisplayChangePaymentMethodButton,
            order,
        },
        ref,
    ) => {
        // if (typeof (lyria_debug) === 'undefined') {
        //     console.log = console.debug = console.info = function () { };
        // }
        const loaderComponent = loader !== undefined ? loader : DEFAULT_LOADER;
        const [loading, setLoading] = React.useState(true);
        const [paymentMethods, setPaymentMethods] = React.useState([]);
        const [expandedPaymentMethodId, setExpandedPaymentMethodId] = React.useState(null);
        const [expandedPaymentMethodByDefaultDone, setExpandedPaymentMethodByDefaultDone] = React.useState(false);
        const [inProgressPaymentMethodId, setInProgressPaymentMethodId] = React.useState(null);
        const [errorCode, setErrorCode] = React.useState(null);
        const expandedPaymentMethodRef = React.useRef();
        const expressCheckoutAppleRef = React.useRef();
        const expressCheckoutGoogleRef = React.useRef();
        const [googleLoaded, setGoogleLoaded] = React.useState(false);
        const [googlePayAllowed, setGooglePayAllowed] = React.useState(false);
        const [initializationErrorsByProvider, setInitializationErrorsByProvider] = React.useState({});

        const displaySpinner = loading;
        const paymentMethodsToDisplay = paymentMethods;
        const showSaveCardMessage = useShowSaveCard();
        const { t } = useTranslation();

        React.useEffect(() => {
            const googleLib = document.createElement('script');
            googleLib.src = 'https://pay.google.com/gp/p/js/pay.js';
            googleLib.async = true;
            googleLib.onload = () => {
                setGoogleLoaded(true);
            };
            document.head.appendChild(googleLib);
            return () => {
                document.head.removeChild(googleLib);
            };
        }, []);
        // preload adyen
        // it's a big chunk of code so we don't want to include it in the main js file
        // but we don't want to wait until we need to show the component either
        React.useEffect(() => {
            importAdyen();
        }, []);

        // handle both fr_FR and fr-FR
        React.useEffect(() => {
            if (lang) {
                i18n.changeLanguage(lang.replace('_', '-'));
            }
        }, [lang]);

        /* Expose functions and props to external components */
        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    return (
                        expandedPaymentMethodRef.current &&
                        expandedPaymentMethodRef.current.pay()
                    );
                },
                isAPaymentMethodExpanded: !!expandedPaymentMethodId,
                canClickOnPay: !!expandedPaymentMethodRef?.current?.canClickOnPay,
            }),
            [expandedPaymentMethodId],
        );


        /* Get the list of available payment methods from lyria backend according to the input token */
        React.useEffect(() => {
            setLoading(true);
            if ((!token && !paymentRequestId) || !lyriaUrl) {
                console.error('"token" and "lyriaUrl" are required');
                return;
            }
            if (!paymentRequestId) {
                return;
            }

            setErrorCode(null);
            axios({
                method: 'post',
                url: `${lyriaUrl}/paymentConfig`,
                data: {
                    request_token: token,
                    payment_request_id: paymentRequestId,
                    client_capabilities: getClientCapabilities(),
                },
            })
                .then((paymentConfigData) => {
                    setPaymentMethods(paymentConfigData.data);
                    onConfigReady?.(paymentConfigData.data);
                    setLoading(false);
                    handlePreviousPaymentAbortedIfNeeded({
                        response: paymentConfigData,
                        lyriaUrl,
                        paymentRequestId,
                    });
                })
                .catch((error) => {
                    console.error(error);
                    if (error.response?.data?.code === 'payment_validated') {
                        onPaymentResult({ success: true, data: { code: error.response?.data?.code }, donationEnabled: error.response?.data?.donations_enabled });
                    }
                    if (error.response?.data?.code === 'payment_being_processed') {
                        onPaymentResult({
                            success: false,
                            data: { code: error.response?.data?.code },
                        });
                    }
                    if (error.response?.data?.code === 'order_is_cancelled') {
                        onPaymentResult({
                            success: false, data: { code: error.response?.data?.code },
                        });
                    }
                    setLoading(false);
                    // setErrorCode('internal');
                });
        }, [token, lyriaUrl, onConfigReady, paymentRequestId, onPaymentResult]);

        React.useEffect(() => {
            if (!googleLoaded) {
                setGooglePayAllowed(false);
                return;
            }
            let googlepayPaymentMethod = paymentMethods.filter((paymentMethod) => GOOGLE_PAYMENT_METHODS.includes(paymentMethod.config?.type));
            if (googlepayPaymentMethod.length === 0) {
                setGooglePayAllowed(false);
                console.log('no gpaymethod');
                return;
            }
            if (googlepayPaymentMethod.length > 1) {
                console.log('too many google pay providers');
            }
            let gpayPaymentMethod = googlepayPaymentMethod[0];

            if (!gpayPaymentMethod.config?.config) {
                setGooglePayAllowed(false);
                console.log('no config for this payment method');
                return;
            }

            const { environment, ...request } = gpayPaymentMethod.config.config.request;
            if (!request.allowedPaymentMethods || request.allowedPaymentMethods.length === 0) {
                console.log('no allowed gpay methods');
                return null;
            }
            let allowedGooglePayMethods = request.allowedPaymentMethods.map((element) => {
                return element.type;
            });
            const paymentsClient = new google.payments.api.PaymentsClient({ environment: environment });
            paymentsClient.isReadyToPay({ allowedPaymentMethods: allowedGooglePayMethods }).then(
                (response) => {
                    if (response.result) {
                        setGooglePayAllowed(true);
                    } else {
                        setGooglePayAllowed(false);
                    }
                })
                .catch(function (_err) {
                    setGooglePayAllowed(false);
                },
                );
        }, [googleLoaded, paymentMethods]);


        const sendErrorToLyria = React.useCallback(({ error_message, response }) => {
            let data = {
                request_token: token,
                payment_request_id: paymentRequestId,
                error_message: error_message ?? response?.data?.message ?? '',
                query_action: (() => {
                    if (!response?.data?.action) return '';
                    try {
                        const str = JSON.stringify(response.data.action);
                        return typeof str === 'string' ? str : '';
                    } catch (error) {
                        console.error('Failed to stringify action:', error);
                        return '';
                    }
                })(),
                url: response ? response?.data?.callback_url : '',
            };
            // if we cannot extract anyting useful, just put the whole response's body in the error_message
            if (data['error_message'] === '' && data['query_action'] === '') {
                try {
                    data['error_message'] = response.text();
                } catch (error) {
                    console.error(error);
                }
            }
            axios({
                method: 'post',
                url: `${lyriaUrl}/payments/error`,
                data: data,
            });
        }, [lyriaUrl, paymentRequestId, token]);


        const onErrorsReloadPaymentConfig = React.useCallback(
            (errorCode) => {
                setLoading(true);
                if (!RETRIABLE_ERRORS.includes(errorCode)) {
                    return;
                }
                setDisplayCallbackBlock(false);
                axios({
                    method: 'post',
                    url: `${lyriaUrl}/paymentConfig`,
                    data: {
                        request_token: token,
                        payment_request_id: paymentRequestId,
                        client_capabilities: getClientCapabilities(),
                    },
                })
                    .then((paymentConfigData) => {
                        setPaymentMethods(paymentConfigData.data);
                        setLoading(false);
                        handlePreviousPaymentAbortedIfNeeded({
                            response: paymentConfigData,
                            lyriaUrl,
                            paymentRequestId,
                        });
                    })
                    .catch((error) => {
                        setLoading(false);
                        sendErrorToLyria({ response: error.response });
                        if (error.response?.data?.code==='order_is_cancelled') {
                            onPaymentResult({
                                success: false, data: {code: error.response?.data?.code},
                            });
                        } else if (error.response?.data?.code === 'payment_being_processed') {
                            onPaymentResult({
                                success: false,
                                data: { code: error.response?.data?.code },
                            });
                        } else {
                            setErrorCode('internal');
                        }
                    });
            },
            [onPaymentResult, token, lyriaUrl, paymentRequestId, setDisplayCallbackBlock, sendErrorToLyria],
        );

        const onClickPaymentMethodOption = React.useCallback(
            (paymentMethod) => (expanded) => {
                // We lock the expanded button to not be expandable or collapsable until payment process finished.
                if (inProgressPaymentMethodId) {
                    return null;
                }
                if (expanded) {
                    setExpandedPaymentMethodId(paymentMethod.id);
                } else {
                    setExpandedPaymentMethodId(null);
                }
            },
            [inProgressPaymentMethodId],
        );

        const paymentResultCallback = React.useCallback(
            (res) => {
                let reloadConfig = true;
                if (res.success) {
                    /* If the payment successes we display a loader because we don't want the user
                    to see the payment form again until the host redirects to its own success page */
                    setErrorCode(null);
                } else {
                    if (res.data?.code === 'initializationError') {
                        setInitializationErrorsByProvider(prev => {
                            const prevCount = prev[res.data.pspId]?.errorsCount ?? 0;
                            const newCount = prevCount + 1;
                            if (newCount > RETRY_ATTEMPTS_FAILED_INITIALIZATION) {
                                reloadConfig = false;
                            }
                            return {
                                ...prev,
                                [res.data.pspId]: { errorsCount: newCount },
                            };
                        });
                    } else {
                        setErrorCode(res.data?.code);
                    }
                    // Send logs to lyria
                    sendErrorToLyria({ response: res, error_message: res.data?.error_message ?? '' });
                    if (reloadConfig) {
                        /* If the payment failed we want to show the other psps expand buttons again if any
                            to allow the user to select another payment method if needed */
                        setInProgressPaymentMethodId(null);
                        // reload payment config if failed
                        onErrorsReloadPaymentConfig(res.data?.code);
                    }
                }
                onPaymentResult(res);
            },
            [onPaymentResult, sendErrorToLyria, onErrorsReloadPaymentConfig],
        );

        const paymentStartedCallback = React.useCallback(
            (paymentMethod) => () => {
                setErrorCode(null);
                setInProgressPaymentMethodId(paymentMethod.id);
            },
            [],
        );

        const getAdyenComponent = React.useCallback(
            (paymentMethod, isExpressApple = false, isExpressGoogle = false) => {
                // todo : filter to display only one adyen payment method, not a dozen since this must be either
                // Gp or apple pay
                let adyenRef;
                switch (true) {
                    case isExpressApple:
                        adyenRef = expressCheckoutAppleRef;
                        break;
                    case isExpressGoogle:
                        adyenRef = expressCheckoutGoogleRef;
                        break;
                    default:
                        adyenRef = expandedPaymentMethodRef;
                }
                let expressCheckout = isExpressApple || isExpressGoogle;

                return (
                    <React.Suspense fallback={<>{loaderComponent}</>}>
                        <Adyen
                            config={paymentMethod.config}
                            token={token}
                            paymentMethodId={paymentMethod.id}
                            paymentRequestId={paymentRequestId}
                            lyriaUrl={lyriaUrl}
                            onPaymentResult={paymentResultCallback}
                            setInProgressPaymentMethodId={setInProgressPaymentMethodId}
                            hidePayButton={hidePayButton}
                            labelPayButton={labelPayButton}
                            labelPayButtonApplePayAndGooglePay={labelPayButtonApplePayAndGooglePay}
                            ref={adyenRef}
                            loader={loaderComponent}
                            savePaymentMethod={paymentMethod.save_payment_method}
                            savedPaymentMethods={paymentMethod.saved_payment_methods}
                            paymentCardInputWarningMessage={expressCheckout ? false : paymentCardInputWarningMessage}
                            operation={paymentMethod.operation}
                            paymentMethods={paymentMethod.payment_methods}
                            locale={lang}
                            themeMode={themeMode}
                            donationEnabled={paymentMethod.donations_enabled}
                            sendErrorToLyria={sendErrorToLyria}
                            setErrorCode={setErrorCode}
                            setDisplayChangePaymentMethodButton={setDisplayChangePaymentMethodButton}
                        />
                    </React.Suspense>
                );
            },
            [loaderComponent, token, paymentRequestId, lyriaUrl, paymentResultCallback, hidePayButton, labelPayButton, labelPayButtonApplePayAndGooglePay, paymentCardInputWarningMessage, lang, themeMode, sendErrorToLyria, setDisplayChangePaymentMethodButton],
        );

        let paymentMethodsToDisplayInOption = paymentMethodsToDisplay;
        let paymentMethodsToDisplayInExpressCheckout = [];
        /* Here filter the payment methods that should appear in a dedicated 'express checkout' section */
        if (!forceDisplayPaymentMethodsInExpandableButton) {
            paymentMethodsToDisplayInOption = paymentMethodsToDisplay.filter((paymentMethod) => !EXPRESS_PAYMENT_METHODS.includes(paymentMethod.config?.type));
            paymentMethodsToDisplayInExpressCheckout = paymentMethodsToDisplay
                .filter((paymentMethod) => EXPRESS_PAYMENT_METHODS.includes(paymentMethod.config?.type))
                .map((paymentMethod, _index, _array) => {
                    if (paymentMethod.outage) return null;
                    const isPaymentMethodOptionApplePay = APPLE_PAYMENT_METHODS.includes(paymentMethod.config?.type);
                    const isPaymentMethodOptionGooglePay = GOOGLE_PAYMENT_METHODS.includes(paymentMethod.config?.type);
                    let isSecureContextValue = true;
                    try {
                        isSecureContextValue = window.isSecureContext || isSecureContext;
                        // eslint-disable-next-line no-unused-vars
                    } catch (e) {
                        // That means the browser does not support it
                    }
                    if (isPaymentMethodOptionGooglePay) {
                        if (!isSecureContextValue) {
                            return null;
                        }
                        if (!googlePayAllowed) {
                            return null;
                        }
                    }
                    switch (paymentMethod.psp) {
                        case 'payline':
                            if (isPaymentMethodOptionApplePay) {
                                const ApplePaySession = 'ApplePaySession' in window ? window['ApplePaySession'] : null;
                                const isInIframe = window.location !== window.top.location;
                                if (!ApplePaySession || !ApplePaySession?.isNotWebkit || isInIframe) {
                                    return null;
                                }
                            }
                            return (
                                <Payline
                                    paymentMethodId={paymentMethod.id}
                                    labelPayButton={labelPayButton}
                                    labelPayButtonApplePayAndGooglePay={labelPayButtonApplePayAndGooglePay}
                                    config={paymentMethod.config}
                                    lang={lang}
                                    token={token}
                                    paymentRequestId={paymentRequestId}
                                    lyriaUrl={lyriaUrl}
                                    onPaymentStarted={paymentStartedCallback(paymentMethod)}
                                    onPaymentResult={paymentResultCallback}
                                    hidePayButton={hidePayButton}
                                    loader={loaderComponent}
                                    savePaymentMethod={paymentMethod.save_payment_method}
                                    savedPaymentMethods={paymentMethod.saved_payment_methods}
                                    operation={paymentMethod.operation}
                                    themeMode={themeMode}
                                    isDisabled={(
                                        !!inProgressPaymentMethodId
                                        && inProgressPaymentMethodId !== paymentMethod.id
                                    )}
                                    force3dsRedirect={paymentMethod.force_3ds_redirect ?? false}
                                />
                            );
                        case 'adyen':
                            // TODO remove this if to integrate this into the «if» block before the switch
                            // then test that nothing got broken
                            if (isPaymentMethodOptionApplePay || isPaymentMethodOptionGooglePay) {
                                return getAdyenComponent(paymentMethod, isPaymentMethodOptionApplePay, isPaymentMethodOptionGooglePay);
                            } else {
                                return null;
                            }
                        case 'revolut':
                            // if we want to display as an express checkout
                            return (<RevolutPay
                                config={paymentMethod.config}
                                token={token}
                                paymentRequestId={paymentRequestId}
                                lyriaUrl={lyriaUrl}
                                savePaymentMethod={paymentMethod.save_payment_method}
                                savedPaymentMethods={paymentMethod.saved_payment_methods}
                                onPaymentStarted={paymentStartedCallback(paymentMethod)}
                                onPaymentResult={paymentResultCallback}
                                loader={loader}
                                hidePayButton={hidePayButton}
                                labelPayButton={labelPayButton}
                                paymentCardInputWarningMessage={paymentCardInputWarningMessage}
                                themeMode={themeMode}
                                successCallbackUrl={successCallbackUrl}
                                failureCallbackUrl={failureCallbackUrl}
                                lineItemName={bankStatement}
                            />);
                        case 'link':
                            return <LinkPSP config={paymentMethod.config} />;
                        default:
                            return null;
                    }
                })
                .filter((paymentMethodComponent) => !!paymentMethodComponent);
        }
        const isExpressCheckoutAvailable = paymentMethodsToDisplayInExpressCheckout.length > 0;

        /**
         *  By default, expand the button if there is only one payment method available
         *  If there are several PSP's with expand=true, the first one is expanded
         */
        React.useEffect(
            () => {
                const paymentMethodsThatCanBeExpanded = paymentMethodsToDisplay?.filter((pm) => !EXPRESS_PAYMENT_METHODS.includes(pm.config?.type));
                if (expandOptionIfOnlyOne && paymentMethodsThatCanBeExpanded.length === 1 && !expandedPaymentMethodByDefaultDone) {
                    if (!paymentMethodsThatCanBeExpanded[0].is_disabled && !isExpressCheckoutAvailable) {
                        setExpandedPaymentMethodId(paymentMethodsThatCanBeExpanded[0].id);
                    }
                    setExpandedPaymentMethodByDefaultDone(true);
                }
                if (paymentMethodsThatCanBeExpanded.length > 1) {
                    const firstExpandableId = paymentMethodsThatCanBeExpanded.find(method => method.expand)?.id;
                    setExpandedPaymentMethodId(firstExpandableId);
                }

            },
            [
                expandOptionIfOnlyOne,
                expandedPaymentMethodByDefaultDone,
                paymentMethodsToDisplay,
                isExpressCheckoutAvailable,
            ],
        );

        const isSandboxMode = useIsSandboxMode();

        return (
            <>
                <link
                    href="https://fonts.googleapis.com/css2?family=Lato&family=Roboto:wght@400;500;700&display=swap"
                    rel="stylesheet"
                />
                <style>
                    {
                        `:root {
                            --primary: ${primaryColor};
                            --primary-dark: ${lighenOrDarkenColor(primaryColor, 0.3)};
                            --primary-light: ${lighenOrDarkenColor(primaryColor, -0.6)};
                            --primary-background: ${lighenOrDarkenColor(primaryColor, -0.9)};
                            --primary-contrast-text: ${getContrastText(primaryColor)};
                            --background-main: ${themeMode === 'dark' ? '#222222' : '#ffffff'};
                            --background-main-disabled: ${themeMode === 'dark' ? '#444444' : '#eeeeee'};
                            --background-card: ${themeMode === 'dark' ? '#393939' : '#f6f6f6'};
                            --background-input: ${themeMode === 'dark' ? '#444444' : '#eeeeee'};
                            --text-main: ${themeMode === 'dark' ? '#ffffff' : '#000000'};
                        }`
                    }
                </style>
                {isSandboxMode && <h2>Sandbox mode</h2>}
                <div id="lyria_pay">
                    {errorCode && (
                        <ErrorBlock errorCode={errorCode} />
                    )}
                    {displaySpinner && (
                        <div className={styles.loader}>
                            {loaderComponent}
                        </div>
                    )}
                    {!displaySpinner && isExpressCheckoutAvailable && (
                        <div className={styles.express_checkout}>
                            <PaymentMethodOptionExpress
                                shouldDisplayExpressCheckoutTitle={shouldDisplayExpressCheckoutTitle}
                                paymentMethodComponents={paymentMethodsToDisplayInExpressCheckout}
                            />
                        </div>
                    )}
                    {isCheckoutPlan && !displaySpinner && (
                        <BackLink
                            callBackUrl={cancelCallbackUrl ? cancelCallbackUrl : failureCallbackUrl}
                        />
                    )}
                    {!displaySpinner && paymentMethodsToDisplayInOption?.length > 0 && !NOT_PAYABLE_ORDER_STATUSES.includes(order?.status) && (
                        <>
                            <div className={styles.regular_checkout}>
                                {shouldDisplayPaymentMethodsTitle && (
                                    <div className={styles.label}>
                                        {t('selectPaymentMethod')}
                                    </div>
                                )}
                                {paymentMethodsToDisplayInOption.map((paymentMethod, index, array) => {
                                    let paymentMethodComponent;
                                    let paymentMethodOoptionIsCreditCard = paymentMethod.payment_methods?.every(p => !!p.is_card) ?? false;
                                    let paymentMethodOptionName = (paymentMethodOoptionIsCreditCard && paymentMethod.payment_methods?.length > 1)
                                        ? t('paymentMethodOptionCreditCard')
                                        : (paymentMethod.payment_methods ?? []).map(p => p.label).join(' / ');
                                    // the payment method labels will be displayed if any payment_method has not «is_card» set to true,
                                    // or if there is just one payment method
                                    let paymentMethodOptionIcons = (paymentMethod.payment_methods ?? []).map(p => p.payment_method);
                                    let paymentMethodIsComponentOnly = false;
                                    const isPaymentMethodOptionApplePay = APPLE_PAYMENT_METHODS.includes(paymentMethod.config?.type);
                                    const isPaymentMethodOptionGooglePay = GOOGLE_PAYMENT_METHODS.includes(paymentMethod.config?.type);
                                    if (paymentMethod.outage) {
                                        const otherPspHasCardMethod = array.some(
                                            (pm) => !pm.outage && pm.payment_methods?.some((p) => !!p.is_card),
                                        );
                                        if (otherPspHasCardMethod) {
                                            return null;
                                        }
                                        const isFirstOutage = array.findIndex((pm) => pm.outage) === index;
                                        if (!isFirstOutage) {
                                            return null;
                                        }
                                        const hasOtherPaymentMethods = array.some((pm) => !pm.outage);
                                        return (
                                            <div
                                                key={`payment_method_option_${paymentMethod.id}`}
                                                className={styles.regulad_checkout_option}
                                            >
                                                <OutageBlock
                                                    name={paymentMethodOptionName || undefined}
                                                    icons={paymentMethodOptionIcons}
                                                    hasOtherPaymentMethods={hasOtherPaymentMethods}
                                                />
                                            </div>
                                        );
                                    }
                                    switch (paymentMethod.psp) {
                                        case 'payline':
                                            if (isPaymentMethodOptionGooglePay) {
                                                paymentMethodOptionName = 'Google Pay';
                                                paymentMethodOptionIcons = ['GooglePay'];
                                                let isSecureContextValue = true;
                                                try {
                                                    isSecureContextValue = window.isSecureContext || isSecureContext;
                                                // eslint-disable-next-line no-unused-vars
                                                } catch (e) {
                                                // That means the browser does not support it
                                                }
                                                if (!isSecureContextValue) {
                                                    return null;
                                                }
                                            } else if (isPaymentMethodOptionApplePay) {
                                                const ApplePaySession = 'ApplePaySession' in window ? window['ApplePaySession'] : null;
                                                const isInIframe = window.location !== window.top.location;
                                                if (!ApplePaySession || !ApplePaySession?.isNotWebkit || isInIframe) {
                                                    return null;
                                                }
                                                paymentMethodOptionName = 'Apple Pay';
                                                paymentMethodOptionIcons = ['ApplePay'];
                                            }
                                            paymentMethodComponent = (
                                                <Payline
                                                    paymentMethodId={paymentMethod.id}
                                                    labelPayButton={labelPayButton}
                                                    labelPayButtonApplePayAndGooglePay={labelPayButtonApplePayAndGooglePay}
                                                    lang={lang}
                                                    config={paymentMethod.config}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentStarted={paymentStartedCallback(paymentMethod)}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    ref={isPaymentMethodOptionApplePay || isPaymentMethodOptionGooglePay ? undefined : expandedPaymentMethodRef}
                                                    loader={loaderComponent}
                                                    savePaymentMethod={paymentMethod.save_payment_method}
                                                    savedPaymentMethods={paymentMethod.saved_payment_methods}
                                                    paymentCardInputWarningMessage={paymentCardInputWarningMessage}
                                                    operation={paymentMethod.operation}
                                                    themeMode={themeMode}
                                                    isDisabled={(
                                                        !!inProgressPaymentMethodId
                                                    && inProgressPaymentMethodId !== paymentMethod.id
                                                    )}
                                                    isSavePaymentMethodForced={isCheckoutPlan || paymentMethod.force_save_payment_method}
                                                    force3dsRedirect={paymentMethod.force_3ds_redirect ?? false}
                                                />
                                            );
                                            break;
                                        case 'adyen':
                                            paymentMethodIsComponentOnly = (
                                                array.length === 1
                                            && paymentMethod.operation === 'payment'
                                            );
                                            paymentMethodComponent = getAdyenComponent(paymentMethod);
                                            break;
                                        case 'paymentsos':
                                            paymentMethodComponent = (
                                                <React.Suspense fallback={<>{loaderComponent}</>}>
                                                    <PaymentsOS
                                                        config={paymentMethod.config}
                                                        token={token}
                                                        paymentRequestId={paymentRequestId}
                                                        lyriaUrl={lyriaUrl}
                                                        savePaymentMethod={paymentMethod.save_payment_method}
                                                        savedPaymentMethods={paymentMethod.saved_payment_methods}
                                                        onPaymentStarted={paymentStartedCallback(paymentMethod)}
                                                        onPaymentResult={paymentResultCallback}
                                                        loader={loaderComponent}
                                                        hidePayButton={hidePayButton}
                                                        labelPayButton={labelPayButton}
                                                        paymentCardInputWarningMessage={paymentCardInputWarningMessage}
                                                        themeMode={themeMode}
                                                        ref={expandedPaymentMethodRef}
                                                    />
                                                </React.Suspense>
                                            );
                                            break;
                                        case 'revolut':
                                        // if we want to display as a regular checkout
                                            paymentMethodComponent = (<RevolutPay
                                                config={paymentMethod.config}
                                                token={token}
                                                paymentRequestId={paymentRequestId}
                                                lyriaUrl={lyriaUrl}
                                                savePaymentMethod={paymentMethod.save_payment_method}
                                                savedPaymentMethods={paymentMethod.saved_payment_methods}
                                                onPaymentStarted={paymentStartedCallback(paymentMethod)}
                                                onPaymentResult={paymentResultCallback}
                                                loader={loader}
                                                hidePayButton={hidePayButton}
                                                labelPayButton={labelPayButton}
                                                paymentCardInputWarningMessage={paymentCardInputWarningMessage}
                                                themeMode={themeMode}
                                                successCallbackUrl={successCallbackUrl}
                                                failureCallbackUrl={failureCallbackUrl}
                                                lineItemName={bankStatement}
                                            />);
                                            break;
                                        case 'nuvei':
                                            paymentMethodComponent = (<Nuvei
                                                onPaymentResult={paymentResultCallback}
                                                nuveiConfig={paymentMethod.config}
                                                lyriaUrl={lyriaUrl}
                                                token={token}
                                                pspId={paymentMethod.id}
                                                paymentRequestId={paymentRequestId}
                                                sendErrorToLyria={sendErrorToLyria}
                                                index={index}
                                            />);
                                            break;
                                        case 'link':
                                            paymentMethodComponent = <LinkPSP config={paymentMethod.config} />;
                                            break;
                                        case 'paymentplan':
                                            paymentMethodComponent = (
                                                <PaymentPlan
                                                    config={paymentMethod.config}
                                                    paymentData={paymentRequestToken}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={onPaymentResult}
                                                    paymentRequestId={paymentRequestId}
                                                    lang={lang}
                                                />
                                            );
                                            break;
                                        case 'iban':
                                            paymentMethodComponent = (
                                                <Iban
                                                    pspId={paymentMethod.id}
                                                    currency={paymentMethod.currency}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    config={paymentMethod.config}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'chilean_form':
                                            paymentMethodComponent = (
                                                <ChileanForm
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'clabe':
                                            paymentMethodComponent = (
                                                <Clabe
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'colombian_form':
                                            paymentMethodComponent = (
                                                <ColombianForm
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'international_bank':
                                            paymentMethodComponent = (
                                                <InternationalBank
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'international_transfers_mexico':
                                            paymentMethodComponent = (
                                                <InternationalTransfersMexico
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'paypal':
                                            paymentMethodComponent = (
                                                <PaypalForm
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'peruvian_form':
                                            paymentMethodComponent = (
                                                <PeruvianForm
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'ingenico':
                                            paymentMethodIsComponentOnly = false;
                                            paymentMethodComponent = (
                                                <Ingenico
                                                    config={paymentMethod.config}
                                                    lyriaUrl={lyriaUrl}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    paymentMethods={paymentMethod.payment_methods}
                                                    themeMode={themeMode}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'ingenico_legacy':
                                            paymentMethodIsComponentOnly = false;
                                            paymentMethodComponent = (
                                                <IngenicoLegacy
                                                    config={paymentMethod.config}
                                                    lyriaUrl={lyriaUrl}
                                                    token={token}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    paymentMethods={paymentMethod.payment_methods}
                                                    themeMode={themeMode}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'south_african_form':
                                            paymentMethodComponent = (
                                                <SouthAfricanForm
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        case 'hong_kong_form':
                                            paymentMethodComponent = (
                                                <HongKongForm
                                                    pspId={paymentMethod.id}
                                                    token={token}
                                                    paymentRequestId={paymentRequestId}
                                                    lyriaUrl={lyriaUrl}
                                                    onPaymentResult={paymentResultCallback}
                                                    hidePayButton={hidePayButton}
                                                    labelPayButton={labelPayButton}
                                                    ref={expandedPaymentMethodRef}
                                                />
                                            );
                                            break;
                                        default:
                                            paymentMethodComponent = null;
                                    }
                                    const initializationErrorCount = initializationErrorsByProvider[paymentMethod.id]?.errorsCount ?? 0;
                                    return (
                                        <div
                                            key={`payment_method_option_${paymentMethod.id}`}
                                            className={styles.regulad_checkout_option}
                                        >
                                            <PaymentMethodOption
                                                internalName={paymentMethod.name}
                                                shouldHideTheExpandableButtonWhenOpen={false}
                                                name={paymentMethodOptionName}
                                                icons={paymentMethodOptionIcons}
                                                operationType={paymentMethod.operation}
                                                isDisabled={paymentMethod.is_disabled || !!inProgressPaymentMethodId}
                                                paymentMethods={paymentMethod.payment_methods}
                                                feesAmount={paymentMethod?.fee ? intlPriceFormat(paymentMethod.fee, 2, paymentMethod.currency, lang) : null}
                                                minimumAmount={intlPriceFormat(paymentMethod.min_amount_for_credit_required, 2, paymentMethod.currency, lang)}
                                                paymentMethodComponent={initializationErrorCount > RETRY_ATTEMPTS_FAILED_INITIALIZATION ? <PSPFailedToLoad pspName={paymentMethod.psp}/> : paymentMethodComponent}
                                                onClick={onClickPaymentMethodOption(paymentMethod)}
                                                isSelected={paymentMethod.id === expandedPaymentMethodId || paymentMethod.id === inProgressPaymentMethodId}
                                                isComponentOnly={paymentMethodIsComponentOnly}
                                                themeMode={themeMode}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {showSaveCardMessage && (
                                <StoredPaymentMethod/>
                            )}
                        </>
                    )}
                    {!displaySpinner && !errorCode && (
                        <div className={styles.no_payment_method_available}>
                            {!paymentMethodsToDisplay.length && <Trans i18nKey="no_payment_method_available" />}
                            {NOT_PAYABLE_ORDER_STATUSES.includes(order?.status) && <Trans i18nKey="paymentNotPossible" />}
                        </div>
                    )}
                </div>
                <PaymentFooter />
            </>
        );
    },
);

PspComposer.propTypes = {
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The id of the payment request
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     * If true, it should be handle outside the LyriaPay component thanks to the 'ref' props.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The label of the 'pay' button to display for GogglePay and ApplePay.
     */
    labelPayButtonApplePayAndGooglePay: PropTypes.oneOf([
        'buy',
        'book',
        'donate',
        'order',
        'pay',
        'logoOnly',
    ]),
    /**
     * Whether we want to force all payment methods to be displayed in an expandable button.
     * This is especially for GooglePay and ApplePay.
     */
    forceDisplayPaymentMethodsInExpandableButton: PropTypes.bool,
    /**
     * Whether we want to display the title about express payment method ('express checkout') above the related section.
     */
    shouldDisplayExpressCheckoutTitle: PropTypes.bool,
    /**
     * Whether we want to display the title about regular payment method ('payment methods') above the related section.
     */
    shouldDisplayPaymentMethodsTitle: PropTypes.bool,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
    /**
     * The callback function on paymentConfig, only usefull for the dev env to setup the dev integration
     */
    onConfigReady: PropTypes.func,
    /**
     * The language code to use to translate all the labels of all the components.
     */
    lang: PropTypes.string,
    /**
     * The loader component to use when the payment or drop-in is loading.
     */
    loader: PropTypes.node,
    /**
     * Whether we want to expand the option by default if there is only one available.
     */
    expandOptionIfOnlyOne: PropTypes.bool,
    /**
     * A warning message to display for payment at the card input step (mainly to advise the user to save its payment method).
     */
    paymentCardInputWarningMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * The primary color to use in the whole component (especially for buttons).
     */
    primaryColor: PropTypes.string,
    /**
     * The theme to use (dark or light).
     */
    themeMode: PropTypes.oneOf(['light', 'dark']),
    /**
     * Display or not the callback block
     */
    setDisplayCallbackBlock: PropTypes.func,
    /**
     * Success url to redirect to if the psp requires one, else prefer calling onPaymentResult
     */
    successCallbackUrl: PropTypes.string,
    /**
     * Failure url to redirect to if the psp requires one, else prefer calling onPaymentResult
     */
    failureCallbackUrl: PropTypes.string,
    /**
     * The bank statement to display on the user bank account.
     */
    bankStatement: PropTypes.string,
    /**
     * The payment data object containing email, currency, amount, failure_url, ...
     */
    paymentRequestToken: PropTypes.object,
    /**
     * Indicates if the component is used in the checkout plan page
     */
    isCheckoutPlan: PropTypes.bool,
};

PspComposer.displayName = 'LyriaPay';

export default PspComposer;
